/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import qs from "qs";
import { useEffect, useState } from "react";
import { Dimensions } from "react-native";
import moment from "moment";

import { store } from "store";
import {
  skaleBlack,
  skaleLinkBlue,
  skaleYellow,
  colorWithOpacity,
  upduoGraphColorSet,
} from "config/UpduoColors";
import Hashids from "hashids";
import {
  RecursivePartial,
  PrependSetTo,
  SnakeToCamelCase,
  RelayArray,
  Enum,
} from "./UtilityTypes";
import {
  SummitType,
  TimeWindowType,
  UserType,
  SkaleSessionType,
} from "graphql/internal/generated";

const hashids = new Hashids("Skale Education Inc.", 8);

/**
 * An alias for `Object.keys` but with stronger type guarantees
 */
export function keysOf<T extends {}>(obj: T): Array<keyof T> {
  return Object.keys(obj) as Array<keyof T>;
}

/**
 * An alias for `Object.entries` but with stronger type guarantees
 */
export function entriesOf<T extends {}>(
  obj: T
): { [K in keyof Required<T>]: [K, T[K]] }[keyof T][] {
  return Object.entries(obj) as { [K in keyof T]: [K, T[K]] }[keyof T][];
}

/**
 * Gets the values of an typescript enum as a list.
 */
export function getEnumValues<E extends Enum<E>>(e: E) {
  return (keysOf(e) as Array<keyof E | number>)
    .filter((k): k is keyof E => e[k] === "number")
    .map((k) => e[k]);
}

/**
 * Transforms a symbol by concatenating `SET_` to it. Used to generate redux
 * message types.
 */
export function prependSetTo<T extends string | number | symbol>(
  s: T
): PrependSetTo<T> {
  return `SET_${String(s)}` as PrependSetTo<T>;
}

/**
 * Capitalizes the first letter of a string
 */
export function capitalize<T extends string>(str: T): Capitalize<T> {
  return `${str.charAt(0).toUpperCase()}${str.slice(1)}` as Capitalize<T>;
}

export function snakeToCamelCase<T extends string>(
  str: T
): SnakeToCamelCase<T> {
  const [first, ...rest] = str.toLowerCase().split("_");
  const result = first + rest.map(capitalize).join("");
  return result as SnakeToCamelCase<T>;
}

export const announcementModeList = [
  { id: "POPUP", name: "In-app announcement" },
  { id: "PUSH", name: "Push notification" },
  { id: "BOTH", name: "Both" },
];

export const allowedMatchAlgorithmList = [
  { id: "SMALL_GROUP", name: "Small Groups" },
  {
    id: "SMALL_GROUP_FIRST_LESSON",
    name: "Small Groups, First Lesson Required",
  },
  { id: "NOVEL_LIQUID", name: "Novel Liquidity" },
  { id: "NOVEL_LIQUID_LOC", name: "Novel Liquidity, Separate Locations" },
  { id: "NOVEL_LIQUID_ISO", name: "Novel Liquidity, Isolated Districts" },
  { id: "TRAINER_REQUIRED", name: "Trainers Required" },
];

export enum SummitState {
  ACTIVE,
  NOT_STARTED,
  COMPLETED,
}

export function getSummitState({
  now,
  startDate,
  endDate,
}: {
  now: Date | number;
  startDate: Date;
  endDate: Date;
}) {
  if (now < startDate) return SummitState.NOT_STARTED;
  if (now >= endDate) return SummitState.COMPLETED;
  return SummitState.ACTIVE;
}

/**
 * Given a list of current or upcoming summits, returns the first
 * one which is currently active, if any
 */
export function getActiveSummit<
  T extends Pick<SummitType, "startDate" | "endDate" | "name">
>(activeSummitList: T[]): T | undefined {
  return (
    activeSummitList
      .filter(
        (summit) =>
          getSummitState({
            now: new Date(),
            startDate: new Date(summit.startDate),
            endDate: new Date(summit.endDate),
          }) === SummitState.ACTIVE
      )
      // For performance, this should be a min instead of a sort
      .sort((a, b) => {
        const aDate = new Date(a.startDate);
        const bDate = new Date(b.startDate);
        if (aDate > bDate) return 1;
        if (bDate > aDate) return -1;
        return a.name > b.name ? 1 : -1;
      })
      .at(0)
  );
}

export const monthArray = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTH_NAMES_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function formattedDateString(date: Date | null | undefined) {
  // Takes a JS Date() and returns YYYY-MM-DD in local timezone
  if (!date) {
    return "";
  }
  const year = date.getFullYear();
  let month = `${date.getMonth() + 1}`;
  let day = `${date.getDate()}`;
  if (month.length < 2) month = `0${month}`;
  if (day.length < 2) day = `0${day}`;
  return [year, month, day].join("-");
}

export function formattedDatetimeToDateString(date: Date) {
  // example: converts 2020-12-09T00:31:11.565647+00:00 to 2020-12-09
  const formattedDate = moment(date).format("YYYY-MM-DD");
  return formattedDate;
}

export function formattedDateAndTimeString(
  date: Date,
  showTimezone: boolean = true
) {
  // Fri Dec 04 2020 1:30 AM EST
  if (!date) {
    return "";
  }
  let fullDate = date.toDateString();
  if (!showTimezone) {
    fullDate = date.toDateString().split(" ").slice(1).join(" ");
  }
  const time = formattedReadableTime(date);
  const timezone = getTimezoneAbbreviation();
  if (!showTimezone) {
    return [fullDate, time].join(" ");
  }
  return [fullDate, time, timezone].join(" ");
}

export function formattedFullDateString(date: Date) {
  return date.toLocaleDateString("en-us", {
    weekday: "short",
    month: "short",
    day: "2-digit",
  });
}

export function shortFormattedDateAndTimeString(date: Date) {
  const fullDate = formattedFullDateString(date);
  const time = formattedReadableTime(date);

  return `${fullDate} - ${time}`;
}

export function getOrdinalNumber(value: number) {
  // 2nd, 3rd, 4th, etc.
  const j = value % 10;
  const k = value % 100;
  if (j === 1 && k !== 11) {
    return `${value}st`;
  }
  if (j === 2 && k !== 12) {
    return `${value}nd`;
  }
  if (j === 3 && k !== 13) {
    return `${value}rd`;
  }
  return `${value}th`;
}

export function formattedReadableTime(date: Date) {
  let hour = date.getHours();
  const minute = date.getMinutes();
  // 5:15 PM
  let timezone = " AM";
  if (hour > 11) {
    timezone = " PM";
  }
  if (hour > 12) {
    hour -= 12;
  }
  if (!hour) {
    hour = 12;
  }
  let minuteString = "";
  if (minute < 10) {
    minuteString += "0";
  }
  minuteString += minute.toString();
  return `${hour.toString()}:${minuteString}${timezone}`;
}

export function getTimezoneAbbreviation() {
  let result = "";
  let regexArray: RegExpExecArray | RegExpMatchArray | null;
  try {
    // Chrome, Firefox
    regexArray = /.*\s(.+)/.exec(
      new Date().toLocaleDateString(navigator.language, {
        timeZoneName: "short",
      })
    );
    if (regexArray) {
      result = regexArray[1];
    }
  } catch (e) {
    // IE, some loss in accuracy due to guessing at the abbreviation
    regexArray = new Date().toTimeString().match(/[A-Z](?!.*[(])/g);
    if (regexArray) {
      result = regexArray.join("");
    }
  }
  return result;
}

export function formattedReadableDateString(dateObject: Date) {
  const month = monthArray[dateObject.getMonth()];
  const day = getOrdinalNumber(dateObject.getDate());
  const year = dateObject.getFullYear();
  return `${month} ${day} ${year}`;
}

export function formattedReadableDateStringMonthDay(dateObject: Date) {
  const month = monthArray[dateObject.getMonth()];
  const day = getOrdinalNumber(dateObject.getDate());
  return `${month} ${day}`;
}

export function formatedReadableDateStringShort(dateObject: Date) {
  const month = MONTH_NAMES_SHORT[dateObject.getMonth()];
  const day = dateObject.getDate();
  return `${month} ${day}`;
}

export function isSameDay(dateA: Date, dateB: Date) {
  const isSameYear = dateA.getFullYear() === dateB.getFullYear();
  const isSameMonth = dateA.getMonth() === dateB.getMonth();
  const isSameDay = dateA.getDate() === dateB.getDate();
  return isSameYear && isSameMonth && isSameDay;
}

export function formattedSummitDateRangeString(
  startDate?: Date,
  endDate?: Date
) {
  if (!startDate || !endDate) {
    // TODO does this ever come up? should it be handled differently?
    return "";
  }
  const startTime = formattedReadableTime(startDate);
  const endTime = formattedReadableTime(endDate);
  const timezoneAbbreviation = getTimezoneAbbreviation();
  const formattedStartDay = formattedReadableDateString(startDate);
  if (isSameDay(startDate, endDate)) {
    return `${formattedStartDay} - ${startTime}-${endTime} ${timezoneAbbreviation}`;
  }
  const formattedEndDay = formattedReadableDateString(endDate);
  const startString = `${formattedStartDay} - ${startTime} ${timezoneAbbreviation}`;
  const endString = `${formattedEndDay} - ${endTime} ${timezoneAbbreviation}`;
  return `${startString} to\n${endString}`;
}

export function formattedTimeString(secondsValue: number) {
  const hours = Math.floor(secondsValue / 3600);
  const minutes = Math.floor(secondsValue / 60) - hours * 60;
  const seconds = secondsValue - minutes * 60;
  const hoursString = hours ? `${hours}:` : "";
  const minutesString = hours ? String(minutes).padStart(2, "0") : minutes;
  const secondsString = String(seconds).padStart(2, "0");
  return `${hoursString}${minutesString}:${secondsString}`;
}

export function formattedTotalConversationTimeString(secondsValue: number) {
  const hours = Math.floor(secondsValue / 3600);
  const minutes = Math.floor(secondsValue / 60) - hours * 60;
  const hoursString = hours === 1 ? `${hours} hour` : `${hours} hours`;
  const minutesString =
    minutes === 1 ? `${minutes} minute` : `${minutes} minutes`;
  const timeString = hours
    ? `${hoursString} and ${minutesString}`
    : minutesString;
  return timeString;
}

export function formattedDuration(seconds: number): [string, string] {
  if (!seconds) {
    return ["0", "seconds"];
  }
  if (seconds >= 60 && seconds < 3600) {
    const minutes = seconds / 60;
    return [minutes.toFixed(0), "minutes"];
  } else if (seconds >= 3600) {
    const hours = seconds / 3600;
    return [hours.toFixed(0), "hours"];
  }
  return [String(seconds), "seconds"];
}

export function formattedSummitDuration(seconds: number) {
  if (seconds < 3600) {
    const minutes = seconds / 60;
    return Math.round(minutes) === 1
      ? [`${minutes.toFixed(0)} minute`]
      : [`${minutes.toFixed(0)} minutes`];
  } else if (seconds >= 3600) {
    const hours = seconds / 3600;
    return Math.round(hours) === 1
      ? [`${hours.toFixed(0)} hour`]
      : [`${hours.toFixed(0)} hours`];
  }
  return [`${0} minutes`];
}

export function convertTimeStringToDateType(timeString: string) {
  const date = new Date();
  const Regex = /(\d+):(\d\d\.?\d*)/.exec(timeString)!;
  const [, hours, minutes] = Regex;
  date.setHours(Number(hours));
  date.setMinutes(Number(minutes));
  return date;
}

export function convertDateTypeToTimeString(date: Date) {
  // example: convert Date Type "Fri Aug 01 2022 08:00:00 GMT-0700" to String Type "08:00:00"
  const hour = date.getHours();
  const minute = date.getMinutes();
  let hourString = "";
  if (hour < 10) {
    hourString += "0";
  }
  hourString += hour.toString();
  let minuteString = "";
  if (minute < 10) {
    minuteString += "0";
  }
  minuteString += minute.toString();
  return `${hourString}:${minuteString}:00`;
}

export function formattedTimeWindowTime(timeWindow: TimeWindowType) {
  // example: converts "06:30:00", "17:00:00" to "6:30AM - 5:00PM"
  const startDate = convertTimeStringToDateType(timeWindow.startTime);
  const endDate = convertTimeStringToDateType(timeWindow.endTime);
  const startTimeString = formattedReadableTime(startDate);
  const endTimeString = formattedReadableTime(endDate);
  return `${startTimeString} - ${endTimeString}`;
}

export function getTimeZoneOffsetDate(
  timeString: string,
  timeZoneOffset: number
) {
  const date = convertTimeStringToDateType(timeString);
  const offsetHours = Math.floor(timeZoneOffset / 60);
  const offsetMinutes = timeZoneOffset % 60;
  date.setHours(date.getHours() - offsetHours);
  date.setMinutes(date.getMinutes() - offsetMinutes);
  return date;
}

export function implementTimeZoneOffset(
  timeWindowsList: Array<TimeWindowType>
) {
  let timeWindows: Array<TimeWindowType> = [];
  const utcOffset = new Date().getTimezoneOffset();
  for (const timeWindow of timeWindowsList) {
    const timeWindowStartDate = getTimeZoneOffsetDate(
      timeWindow.startTime,
      utcOffset
    );
    const timeWindowEndDate = getTimeZoneOffsetDate(
      timeWindow.endTime,
      utcOffset
    );
    timeWindows = timeWindows.concat([
      {
        __typename: "TimeWindowType",
        id: timeWindow.id,
        startTime: convertDateTypeToTimeString(timeWindowStartDate),
        endTime: convertDateTypeToTimeString(timeWindowEndDate),
        text: timeWindow.text,
        order: timeWindow.order,
      },
    ]);
  }
  return timeWindows;
}

/**
 * Checks if user entry belongs to a maintenance region, district, or store
 */
export function isNotMaintenanceUser(user: RecursivePartial<UserType>) {
  for (const name of [user.name, user.region, user.district, user.store]) {
    if (name === "Maintenance") {
      return false;
    }
  }
  return true;
}

/**
 * Sorts a list of objects by the value of one or more attributes of those objects.
 * If more than one key is provided, subsequent keys will be used as tie breakers, in order
 */
export function sortByKey<
  K extends Array<PropertyKey>,
  T extends {
    [Key in keyof K as Key extends number ? K[Key] : never]:
      | number
      | string
      | boolean;
  }
>(inputObjectArray: T[], ...keys: K): T[] {
  if (inputObjectArray.length < 2) {
    return inputObjectArray;
  }
  return inputObjectArray.sort((a, b) => {
    for (const key of keys) {
      if (a[key] > b[key]) return 1;
      if (a[key] < b[key]) return -1;
    }
    return 0;
  });
}

/**
 * Sorts a list of objects by the value of one or more attributes of those objects.
 * If more than one key is provided, subsequent keys will be used as tie breakers, in order
 */
export function sortByKeyFunction<T>(
  inputObjectArray: T[],
  ...keys: Array<(obj: T) => string | number>
): T[] {
  if (inputObjectArray.length < 2) {
    return inputObjectArray;
  }
  return inputObjectArray.sort((a, b) => {
    for (const key of keys) {
      if (key(a) > key(b)) return 1;
      if (key(a) < key(b)) return -1;
    }
    return 0;
  });
}

export function reorder<T>(
  list: Iterable<T> | ArrayLike<T>,
  startIndex: number,
  endIndex: number
): T[] {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

export function doDetermineIfScrollIsAtBottom(
  window: Window & typeof globalThis,
  document: Document
) {
  const windowHeight =
    "innerHeight" in window
      ? window.innerHeight
      : document.documentElement.offsetHeight;
  const body = document.body;
  const html = document.documentElement;
  const docHeight = Math.max(
    body.scrollHeight,
    body.offsetHeight,
    html.clientHeight,
    html.scrollHeight,
    html.offsetHeight
  );
  // Bottom of window value is padded by 20 pixels to provide margin of error
  // in case the browser reports a slightly different height than the actual height
  const bottomOfWindow = Math.ceil(windowHeight + window.pageYOffset) + 20;
  return bottomOfWindow >= docHeight;
}

export function useOnScrollToBottom(callback: () => void | Promise<void>) {
  const [isAtBottom, setIsAtBottom] = useState(false);
  const _handleScroll = () => {
    setIsAtBottom(doDetermineIfScrollIsAtBottom(window, document));
  };
  useEffect(() => {
    window.addEventListener("scroll", _handleScroll);
    _handleScroll();
    return () => {
      window.removeEventListener("scroll", _handleScroll);
    };
  }, []);
  useEffect(() => {
    if (isAtBottom) {
      const result = callback();
      result
        ? result.finally(() => setIsAtBottom(false))
        : setIsAtBottom(false);
    }
  }, [isAtBottom]);

  return { reCheckPageScroll: _handleScroll };
}

export function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState<{
    width: number;
    height: number;
  }>(Dimensions.get("window"));

  useEffect(() => {
    window.addEventListener("resize", () => {
      setWindowDimensions(Dimensions.get("window"));
    });
    return () => {
      window.removeEventListener("resize", () => {
        setWindowDimensions(Dimensions.get("window"));
      });
    };
  }, []);

  return windowDimensions;
}

export function title(str: string) {
  if (str) {
    return str.replace(/\w\S*/g, function (txt: string) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  } else {
    return "";
  }
}

export function safeDataSetObject(
  isLatest: any,
  data: string | any[],
  dataColor: any,
  dataLabel: any,
  isFilled: any,
  isBottom: any
) {
  let dataObjectMain = {
    lineTension: 0,
    borderColor: dataColor,
    pointBorderColor: dataColor,
    pointBackgroundColor: dataColor,
    backgroundColor: isFilled
      ? colorWithOpacity(dataColor, 0.5)
      : "transparent",
    data: data ? data.slice(0, -1) : null,
    fill: isBottom ? "origin" : "-1",
  };
  let dataObjectTail = {
    lineTension: 0,
    borderColor: isLatest ? colorWithOpacity(dataColor, 0.5) : dataColor,
    pointBorderColor: isLatest ? colorWithOpacity(dataColor, 0.5) : dataColor,
    pointBackgroundColor: isLatest
      ? colorWithOpacity(dataColor, 0.5)
      : dataColor,
    backgroundColor: isFilled
      ? isLatest
        ? colorWithOpacity(dataColor, 0.25)
        : colorWithOpacity(dataColor, 0.5)
      : "transparent",
    data: data
      ? new Array(data.length - 2).fill(undefined).concat(data.slice(-2))
      : null,
    borderDash: isLatest ? [10, 5] : [],
    fill: isBottom ? "origin" : "-1",
  };
  if (dataLabel) {
    dataObjectMain = Object.assign({}, dataObjectMain, { label: dataLabel });
    dataObjectTail = Object.assign({}, dataObjectTail, { label: dataLabel });
  }
  return [dataObjectMain, dataObjectTail];
}

const graphColorSet = shuffleArray(upduoGraphColorSet);

function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

export function getGraphColor(inputString: string, index?: number) {
  if (index === undefined || index >= 10) {
    return stringToColor(inputString);
  }
  return graphColorSet[index];
}

export function stringToColor(inputString: string) {
  let sum = 0;
  for (let i = 0; i < inputString.length; i++) {
    sum += inputString.charCodeAt(i);
  }
  const subvalue = (seed: number) => {
    const x = "0.";
    const y: number = +x.concat(Math.cos(seed).toString().substr(6));
    return ~~(y * 256);
  };
  const r = subvalue(sum + 1);
  const g = subvalue(sum + 2);
  const b = subvalue(sum + 3);
  return `rgb(${r},${g},${b})`;
}

export function randInt(min: number, max: number) {
  const minimum = min ? min : 0;
  const maximum = max ? max : minimum;
  return Math.floor(Math.random() * (maximum - minimum + 1)) + minimum;
}

export function encodeIntToHash(plainInt: string) {
  return hashids.encode(plainInt);
}

export function decodeHashToInt(hashedInt: string) {
  // hashids package always returns as an array
  const intArray = hashids.decode(hashedInt);
  return intArray[0];
}

export function masteryColor(rating: number) {
  let ratingColor = skaleLinkBlue;
  switch (rating) {
    case 3:
      ratingColor = colorWithOpacity("#008000", 0.25);
      break;
    case 2:
      ratingColor = colorWithOpacity(skaleYellow, 0.25);
      break;
    case 1:
      ratingColor = colorWithOpacity("#FF0000", 0.25);
      break;
    default:
      ratingColor = colorWithOpacity(skaleBlack, 0.1);
      break;
  }
  return ratingColor;
}

export function processSkillString(
  rawString: string,
  teacherFirstName = "undefined",
  learnerFirstName = "undefined",
  teacherRole = "undefined",
  learnerRole = "undefined",
  skillName = "undefined",
  isTeacher = true,
  communityName = "undefined"
) {
  const replacements: { [key: string]: string } = {
    "{PARTNER_FIRST_NAME}": isTeacher ? learnerFirstName : teacherFirstName,
    "{SELF_FIRST_NAME}": isTeacher ? teacherFirstName : learnerFirstName,
    "{PARTNER_ROLE}": isTeacher ? learnerRole : teacherRole,
    "{SELF_ROLE}": isTeacher ? teacherRole : learnerRole,
    "{P1_FIRST_NAME}": teacherFirstName,
    "{P2_FIRST_NAME}": learnerFirstName,
    "{P1_ROLE}": teacherRole,
    "{P2_ROLE}": learnerRole,
    "{LESSON_NAME}": skillName,
    "{COMMUNITY_NAME}": communityName,
    "{ORGANIZATION_NAME}": communityName,
  };
  let processedString = rawString;
  for (const [key, value] of Object.entries(replacements)) {
    processedString = processedString.replace(key, value);
  }
  return processedString;
}

export function getQueryString(
  parameter: string | Record<string, unknown> | Object | null
) {
  const reduxState = store.getState() as any;
  // Generates API call's query string that supplies filtering parameters
  const queryObject: any = {
    start_date: formattedDateString(reduxState.dayRangeStart),
    end_date: formattedDateString(reduxState.dayRangeEnd),
  };
  if (typeof parameter === "string") {
    // Treat argument as Breakdown's time series query param
    queryObject.query_type = parameter;
    queryObject.time_bin = "day";
  } else if (typeof parameter === "number") {
    // Treat argument as Activity's pagination query param
    queryObject.page = parameter;
  } else if (typeof parameter === "object") {
    Object.assign(queryObject, parameter);
  }
  if (reduxState.filterSkillId) {
    queryObject.skill_id = reduxState.filterSkillId;
  }
  // Only use most specific category's filter
  if (reduxState.filterUserId && !queryObject.user_id) {
    queryObject.user_id = reduxState.filterUserId;
  } else if (reduxState.filterStoreId && !queryObject.store_id) {
    queryObject.store_id = reduxState.filterStoreId;
  } else if (reduxState.filterDistrictId && !queryObject.district_id) {
    queryObject.district_id = reduxState.filterDistrictId;
  } else if (reduxState.filterRegionId && !queryObject.region_id) {
    queryObject.region_id = reduxState.filterRegionId;
  }
  return `?${qs.stringify(queryObject)}`;
}

export function validateEmail(email: string) {
  let isEmailFormat = false;
  const regexPattern = /.+@.+\..+/g;
  if (regexPattern.test(email)) {
    isEmailFormat = true;
  }
  return isEmailFormat;
}

export function generateCountdownString(date: Date, now: Date = new Date()) {
  let countdownSeconds = (date.getTime() - now.getTime()) / 1000;
  if (countdownSeconds === 0) {
    return "🚀  Join now!";
  }
  let result = "Starts in: ";
  const daySeconds = 86400;
  const hourSeconds = 3600;
  const minuteSeconds = 60;
  if (countdownSeconds >= daySeconds) {
    const days = Math.floor(countdownSeconds / daySeconds);
    countdownSeconds = countdownSeconds - days * daySeconds;
    result += `${days}d `;
  }
  if (countdownSeconds >= hourSeconds) {
    const hours = Math.floor(countdownSeconds / hourSeconds);
    countdownSeconds = countdownSeconds - hours * hourSeconds;
    result += `${hours}hr `;
  }
  if (countdownSeconds >= minuteSeconds) {
    const minutes = Math.floor(countdownSeconds / minuteSeconds);
    countdownSeconds = countdownSeconds - minutes * minuteSeconds;
    result += `${minutes}m `;
  }
  const seconds = Math.floor(countdownSeconds);
  result += `${seconds}s`;
  return result;
}

export function getSummitStatus(summit?: {
  startDate: string;
  endDate: string;
}): "before" | "live" | "ended" {
  if (!summit) {
    const reduxState = store.getState() as any;
    summit = reduxState.selectedSummit;
  }
  if (summit?.startDate && summit?.endDate) {
    const startDateObject = new Date(Date.parse(summit?.startDate));
    const now = new Date();
    if (now < startDateObject) {
      return "before";
    }
    const endDateObject = new Date(Date.parse(summit?.endDate));
    if (now >= startDateObject && now < endDateObject) {
      return "live";
    }
  }
  return "ended";
}

export function download(data: BlobPart, filename: string, type: string) {
  const file = new Blob([data], { type: type });
  const a = document.createElement("a"),
    url = URL.createObjectURL(file);
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(function () {
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, 0);
}

export function copyToClipboard(content: string) {
  navigator.clipboard.writeText(content);
}

export function b64toBlob(b64Data: string, contentType = "", sliceSize = 512) {
  const byteCharacters = atob(b64Data);
  const byteArrays = new Array<Uint8Array>();
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }
  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
}

export function hash(str: string) {
  let hash = 0;
  if (str.length === 0) return hash;

  for (let i = 0; i < str.length; i++) {
    const chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }

  return hash;
}

export enum SummitColor {
  ORANGE,
  BLUE,
  PURPLE,
}

export function getSummitColor(
  summit: Pick<SummitType, never> // eslint-disable-line @typescript-eslint/no-unused-vars
): SummitColor {
  return SummitColor.BLUE;
  // return [SummitColor.ORANGE, SummitColor.BLUE, SummitColor.PURPLE][
  //   hash(summit.id) % 3
  // ];
}

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function clamp(x: number, min: number, max: number) {
  return x < min ? min : x > max ? max : x;
}

/**
 * Returns `true` if the input is `null` or `undefined`
 */
export function isNil<T>(x: T | null | undefined): x is null | undefined {
  return x === null || x === undefined;
}

/**
 * Returns `true` if the input is anything other than `null` or `undefined`.
 * Importantly, the return type communicates this so TypeScript understands.
 * This function can be used to e.g. filter out nulls from a list and it
 * will change the type of the list accordingly.
 */
export function isNotNil<T>(x: T | null | undefined): x is T {
  return x !== null && x !== undefined;
}

export function getNodes<T>(data: RelayArray<T>): T[] {
  const edges = data?.edges || [];
  return edges.map((x) => x?.node).filter(isNotNil);
}

export function fmtMSS(s: number) {
  const minutes = Math.floor(s / 60);
  const seconds = String(s % 60).padStart(2, "0");
  return `${minutes}:${seconds}s`;
}

export function getTranscriptJson(
  session: Pick<SkaleSessionType, "transcriptionUrl">
) {
  return fetch(session?.transcriptionUrl).then((res) => res.json());
}

/**
 * Get a CDN url for the image at the specified size. Must specify one of width or height.
 * @param url The original, absolute source url of the image (need not be on our domain.)
 * @param width The width of the image, or 0 to use a width proportional to the height.
 * @param height The height of the image, or 0 to use a height proportional to the width.
 * @returns The CDN url for the image at the specified size.
 */
export function getResizedImageUrl(url: string, width = 0, height = 0): string {
  if (width === 0 && height === 0) {
    throw new Error("Must specify one of width or height");
  }
  if (url.endsWith(".svg")) {
    return url;
  }

  let options = "";
  if (width > 0) {
    options = `width=${width}`;
  }
  if (height > 0) {
    options += `${options && ","}height=${height}`;
  }
  // We're using Cloudflare's image resizer.
  // The options part of the url is a comma-separated list.
  // The url is not encoded.
  // See: https://developers.cloudflare.com/images/image-resizing/url-format/
  return `https://images.upduo.com/cdn-cgi/image/${options}/${url}`;
}

export function getLastDateOfMonth(now: Date = new Date()): Date {
  return new Date(
    new Date(now.getFullYear(), now.getMonth() + 1, 1).getTime() - 1
  );
}

export function removeHTMLTag(str: string): string {
  const regex = /(<([^>]+)>)/gi;
  const newString = str.replace(regex, "");
  return newString;
}

export function modifyToRelay() {}
